// The "Connect <App>" button in the chat.
//
// When the user asks for an app they have not connected, the engine's
// `connect_integration` tool fires (see openanalyst/src/tool/connect-integration.ts).
// The model cannot connect anything itself — it surfaces this affordance, and the human
// consents and authenticates. That boundary is deliberate: an agent must never silently
// acquire access to someone's mailbox.
//
// Clicking runs the SAME flow as Settings → Integrations: open the marketplace/connect
// URL for that provider in an in-app window, wait for the backend to report the new
// connection, then close and toast. The provider's tools are live on the very next turn
// — the engine builds its MCP client off the connected list, so nothing needs a restart.

import { Button } from "@openanalyst-ai/ui/button"
import { Icon } from "@openanalyst-ai/ui/icon"
import { Spinner } from "@openanalyst-ai/ui/spinner"
import { showToast } from "@openanalyst-ai/ui/toast"
import { ToolRegistry } from "@openanalyst-ai/ui/message-part"
import { createSignal, Show } from "solid-js"
import { getMarketplaceUrl } from "@/services/mcp/api"
import { usePlatform } from "@/context/platform"

function ConnectIntegration(props: { metadata: Record<string, unknown> }) {
  const platform = usePlatform()
  const [busy, setBusy] = createSignal(false)

  const name = () => (typeof props.metadata["name"] === "string" ? (props.metadata["name"] as string) : undefined)
  const provider = () =>
    typeof props.metadata["provider"] === "string" ? (props.metadata["provider"] as string) : undefined
  const connectable = () => props.metadata["connectable"] === true && !!provider()

  const connect = async () => {
    const id = provider()
    if (!id || busy()) return
    setBusy(true)
    try {
      const url = await getMarketplaceUrl(id)
      if (!url) {
        showToast({ variant: "error", title: "Could not open the connect page", description: "Please try Settings → Integrations." })
        return
      }
      // Prefer the in-app window so connecting never takes the user out of OpenAnalyst.
      const openWindow = platform.openIntegrationWindow
      if (!openWindow) {
        platform.openLink(url)
        return
      }
      const windowId = await openWindow(url).catch(() => null)
      if (windowId == null) {
        platform.openLink(url)
        return
      }
      // The engine picks the new provider up on the next turn on its own, so we do not
      // poll the tool list here — we only need to close the window and tell the user.
      const unsubscribe = platform.onIntegrationWindowClosed?.((closedId) => {
        if (closedId !== windowId) return
        unsubscribe?.()
        showToast({
          variant: "success",
          icon: "circle-check",
          title: `${name() ?? "Integration"} connected`,
          description: "Ask again and I'll use it.",
        })
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Show
      when={connectable()}
      fallback={
        <div data-connect-integration-empty="">
          <span>Nothing to connect.</span>
        </div>
      }
    >
      <div data-connect-integration="">
        <div data-connect-integration-copy="">
          <Icon name="plugin" />
          <span>
            <b>{name()}</b> isn’t connected yet.
          </span>
        </div>
        <Button size="small" onClick={connect} disabled={busy()}>
          <Show when={busy()} fallback={`Connect ${name()}`}>
            <Spinner />
            Connecting…
          </Show>
        </Button>
      </div>
    </Show>
  )
}

let registered = false

export function registerConnectIntegrationTool() {
  if (registered) return
  ToolRegistry.register({
    name: "connect_integration",
    render(props) {
      return <ConnectIntegration metadata={props.metadata as Record<string, unknown>} />
    },
  })
  registered = true
}

registerConnectIntegrationTool()
